import { ZTooltip } from '@/shared/components/ZTooltip/ZTooltip';
import { GripHorizontal, Info, Trash } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFrameworkWizardStore } from './states';
import { FrameworkField } from './types';

const HeaderWrapper = ({
  children,
  isHeader = true,
}: {
  children: React.ReactNode;
  isHeader?: boolean;
}) => (
  <div
    className={`box-border flex flex-row items-center px-0.5 py-[6px] gap-0.5 w-full h-11 border-b border-[#E4E4E7] shadow-sm ${isHeader ? 'bg-[#F4F4F5]' : 'bg-[#F9F7F7]'
      }`}
  >
    {children}
  </div>
);

const DraggableHeader = ({
  title,
  hasInfo,
  isEditable,
  isDeletable,
  backendKey,
  fieldId,
  isEditing,
  editTitle,
  onTitleChange,
  onEditTitleChange,
  onEditStart,
  onEditEnd,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  title: string;
  hasInfo?: boolean;
  isEditable?: boolean;
  isDeletable?: boolean;
  backendKey?: string;
  _showHandle?: boolean;
  fieldId?: string;
  isEditing?: boolean;
  editTitle?: string;
  onTitleChange?: (newTitle: string) => void;
  onEditTitleChange?: (newTitle: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  onDelete?: () => void;
  onDragStart?: (fieldId: string) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (targetFieldId: string) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { setFirstStepValidationError: _setFirstStepValidationError } =
    useFrameworkWizardStore();

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    if (isEditable && onEditStart) {
      onEditStart();
    }
  };

  const handleTitleSubmit = () => {
    if (onTitleChange && onEditEnd && editTitle !== undefined) {
      const trimmedTitle = editTitle.trim();
      if (trimmedTitle === '' && onDelete && isDeletable) {
        // If the title is empty and the field is deletable, delete the field instead of updating it
        onDelete();
      } else if (trimmedTitle === '' && !isDeletable) {
        // For non-deletable fields (Control ID, Control Description), reset to default values
        const defaultTitle =
          backendKey === 'control_id'
            ? 'Control ID'
            : backendKey === 'control_description'
              ? 'Control Description'
              : title; // fallback to original title
        onTitleChange(defaultTitle);
        onEditEnd();
      } else {
        onTitleChange(trimmedTitle);
        onEditEnd();
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    }
    if (e.key === 'Escape' && onEditEnd && onEditTitleChange) {
      onEditEnd();
      onEditTitleChange(title); // reset
    }
  };

  const handleDragStart = (_e: React.DragEvent) => {
    if (fieldId && onDragStart) {
      onDragStart(fieldId);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (onDragOver) {
      onDragOver(e);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (fieldId && onDrop) {
      onDrop(fieldId);
    }
  };

  return (
    <div
      className={`box-border flex flex-row items-center px-0.5 py-[6px] gap-0.5 w-full h-11 border-b border-[#E4E4E7] shadow-sm bg-[#F4F4F5]`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center">
        <button
          className={`p-0.5 transition-opacity cursor-grab active:cursor-grabbing ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          draggable
          onDragStart={handleDragStart}
          onDragEnd={onDragEnd}
        >
          <GripHorizontal size={16} className="text-[#71717A] -rotate-90" />
        </button>
      </div>
      <div
        className={`box-border flex flex-row items-center p-2 gap-1 flex-grow h-8 rounded ${isEditable && isEditing
            ? 'bg-white border border-[#E4E4E7] cursor-text'
            : ''
          }`}
        onClick={handleTitleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isEditable && isEditing ? (
          <input
            maxLength={25}
            placeholder={
              backendKey === 'control_id'
                ? 'Control ID'
                : backendKey === 'control_description'
                  ? 'Control Description'
                  : backendKey === 'testing_procedures'
                    ? 'Testing Procedures'
                    : 'Add Field Name'
            }
            ref={inputRef}
            type="text"
            value={editTitle}
            onChange={(e) => {
              if (onEditTitleChange) onEditTitleChange(e.target.value);
            }}
            onBlur={handleTitleSubmit}
            onKeyDown={handleKeyPress}
            className={`text-xs font-medium outline-none w-full`}
          />
        ) : (
          <span className="text-xs font-medium text-[#18181B]">{title}</span>
        )}
      </div>
      {isDeletable && (
        <button
          className={`p-0.5 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          onClick={onDelete}
        >
          <Trash size={16} className="text-[#F31260]" />
        </button>
      )}
      {hasInfo && (
        <ZTooltip
          showArrow
          content={
            fieldId === 'control_id'
              ? 'This has to be unique. This is required.'
              : fieldId === 'control_description'
                ? `Description of your control or requirement. This is required`
                : 'Describe how this control will be tested for effectiveness or design.'
          }
          placement="bottom"
          isDisabled={false}
        >
          <button className="p-0.5">
            <Info size={16} className="text-[#A5A5A5]" />
          </button>
        </ZTooltip>
      )}
    </div>
  );
};

const AddColumnHeader = ({ onClick }: { onClick?: () => void }) => (
  <HeaderWrapper isHeader={false}>
    <div
      className="box-border flex flex-row items-center justify-center flex-grow h-8 gap-1 p-2 rounded cursor-pointer hover:bg-gray-50"
      onClick={onClick}
    >
      <span className="text-xs text-[#A1A1AA] flex-grow">Add Field Name</span>
    </div>
  </HeaderWrapper>
);

const Cell = () => (
  <div className="flex justify-center items-center p-2 gap-1 w-full h-9 bg-white border-b border-[#E4E4E7]">
    <span className="text-sm text-[#3F3F46] w-full"></span>
  </div>
);

const Column = ({ header }: { header: React.ReactNode }) => (
  <div className="flex flex-col items-start w-1/5 border-r border-[#E4E4E7] self-stretch flex-shrink-0">
    {header}
    <div className="w-full">
      {Array.from({ length: 17 }).map((_, i) => (
        <Cell key={i} />
      ))}
    </div>
  </div>
);

export default function StepOne() {
  const { stepOneData, updateStepOneData } = useFrameworkWizardStore();
  const [framework, setFramework] = useState<{
    fields: FrameworkField[];
    rows: Record<string, string>[];
  }>({
    fields: stepOneData.fields || [
      {
        id: 'control_id',
        key: 'Control ID',
        ui_key: 'Control ID',
        isMandatory: true,
        isEditable: true,
        hasInfo: true,
      },
      {
        id: 'control_description',
        key: 'Control Description',
        ui_key: 'Control Description',
        isMandatory: true,
        isEditable: true,
        hasInfo: true,
      },
      {
        id: 'testing_procedures',
        key: 'Testing Procedures',
        ui_key: 'Testing Procedures',
        isMandatory: false,
        isEditable: true,
        hasInfo: true,
      },
    ],
    rows: Array.from({ length: 17 }, (_, i) => ({
      id: `${i + 1}`,
      control_description: '',
      testing_procedure: '',
    })),
  });

  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);

  // New: Manage editing state for each field
  const [fieldEditStates, setFieldEditStates] = useState<
    Record<string, { isEditing: boolean; editTitle: string }>
  >(() => {
    const obj: Record<string, { isEditing: boolean; editTitle: string }> = {};
    (
      stepOneData.fields || [
        { id: 'control_id', ui_key: 'Control ID' },
        { id: 'control_description', ui_key: 'Control Description' },
        { id: 'testing_procedures', ui_key: 'Testing Procedures' },
      ]
    ).forEach((f) => {
      obj[f.id] = { isEditing: false, editTitle: f.ui_key };
    });
    return obj;
  });

  // Save fields to store whenever framework changes
  useEffect(() => {
    updateStepOneData({ fields: framework.fields });
  }, [framework.fields, updateStepOneData]);

  // Keep fieldEditStates in sync with framework.fields
  useEffect(() => {
    setFieldEditStates((prev) => {
      const next: Record<string, { isEditing: boolean; editTitle: string }> = {
        ...prev,
      };
      framework.fields.forEach((f) => {
        if (!next[f.id]) {
          next[f.id] = { isEditing: false, editTitle: f.ui_key };
        } else {
          // Update editTitle to match current ui_key when not editing
          if (!next[f.id].isEditing) {
            next[f.id] = { ...next[f.id], editTitle: f.ui_key };
          }
        }
      });
      // Remove deleted fields
      Object.keys(next).forEach((id) => {
        if (!framework.fields.find((f) => f.id === id)) {
          delete next[id];
        }
      });
      return next;
    });
  }, [framework.fields]);

  const addField = () => {
    const newFieldId = `field_${Date.now()}`;
    const newFieldTitle = '';
    const newField: FrameworkField = {
      id: newFieldId,
      key: newFieldTitle, // For new fields, key and ui_key start the same
      ui_key: newFieldTitle,
      isMandatory: false,
      isEditable: true,
    };

    setFramework((prev) => ({
      ...prev,
      fields: [...prev.fields, newField],
      rows: prev.rows.map((row) => ({ ...row, [newFieldId]: '' })),
    }));
    setFieldEditStates((prev) => ({
      ...prev,
      [newFieldId]: { isEditing: true, editTitle: '' },
    }));
    // horizontal scroll to the new field
    const container = document.querySelector('#framework-fields-container');
    if (container) {
      container.scrollLeft = 9999;
    }
  };

  const updateFieldTitle = (fieldId: string, newTitle: string) => {
    setFramework((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => {
        if (field.id === fieldId) {
          return {
            ...field,
            ui_key: newTitle,
            // For non-mandatory fields (except testing_procedures), also update the key
            ...(!field.isMandatory &&
              fieldId !== 'testing_procedures' && { key: newTitle }),
          };
        }
        return field;
      }),
    }));
  };

  const deleteField = (fieldId: string) => {
    setFramework((prev) => ({
      ...prev,
      fields: prev.fields.filter((field) => field.id !== fieldId),
      rows: prev.rows.map((row) => {
        const { [fieldId]: _, ...rest } = row;
        return rest;
      }),
    }));
    setFieldEditStates((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const handleDragStart = (fieldId: string) => {
    setDraggedFieldId(fieldId);
  };

  const handleDragEnd = () => {
    setDraggedFieldId(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetFieldId: string) => {
    if (!draggedFieldId || draggedFieldId === targetFieldId) return;

    const draggedField = framework.fields.find((f) => f.id === draggedFieldId);
    const targetField = framework.fields.find((f) => f.id === targetFieldId);

    if (!draggedField || !targetField) {
      return;
    }

    setFramework((prev) => {
      const newFields = [...prev.fields];
      const draggedIndex = newFields.findIndex((f) => f.id === draggedFieldId);
      const targetIndex = newFields.findIndex((f) => f.id === targetFieldId);

      // Swap the fields
      [newFields[draggedIndex], newFields[targetIndex]] = [
        newFields[targetIndex],
        newFields[draggedIndex],
      ];

      return {
        ...prev,
        fields: newFields,
      };
    });

    setDraggedFieldId(null);
  };

  // Handlers for editing state
  const handleEditStart = (fieldId: string) => {
    setFieldEditStates((prev) => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], isEditing: true },
    }));
  };
  const handleEditEnd = (fieldId: string) => {
    const field = framework.fields.find((f) => f.id === fieldId);
    setFieldEditStates((prev) => ({
      ...prev,
      [fieldId]: {
        isEditing: false,
        editTitle: field?.ui_key || prev[fieldId]?.editTitle || '',
      },
    }));
  };
  const handleEditTitleChange = (fieldId: string, newTitle: string) => {
    setFieldEditStates((prev) => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], editTitle: newTitle },
    }));
  };

  return (
    <div className="flex flex-col items-start self-stretch w-full h-full gap-4">
      <div className="flex flex-row items-center justify-between w-full">
        <p className="text-sm font-semibold text-[#18181B]">
          Define the framework you&apos;d like to assess.
        </p>
        <button
          className="box-border flex flex-row items-center py-1 px-2 gap-1.5 h-6 bg-[#FAFAFA] border border-[#006FEE] rounded-md text-[#006FEE] text-xs font-medium"
          onClick={addField}
        >
          Add Field
        </button>
      </div>

      <div className="box-border flex flex-col items-start w-full bg-white border border-[#E4E4E7] rounded-lg">
        <div
          className="flex flex-row w-full mb-1 overflow-x-scroll scrollbar-gutter-stable"
          id="framework-fields-container"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#D4D4D8 #F4F4F5' }}
        >
          {framework.fields.map((field) => (
            <Column
              key={field.id}
              header={
                <DraggableHeader
                  title={field.ui_key}
                  backendKey={field.id}
                  hasInfo={field.hasInfo}
                  isEditable={field.isEditable}
                  isDeletable={!field.isMandatory}
                  _showHandle={true}
                  fieldId={field.id}
                  isEditing={fieldEditStates[field.id]?.isEditing}
                  editTitle={fieldEditStates[field.id]?.editTitle}
                  onTitleChange={(newTitle) =>
                    updateFieldTitle(field.id, newTitle)
                  }
                  onEditTitleChange={(newTitle) =>
                    handleEditTitleChange(field.id, newTitle)
                  }
                  onEditStart={() => handleEditStart(field.id)}
                  onEditEnd={() => handleEditEnd(field.id)}
                  onDelete={() => deleteField(field.id)}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              }
            />
          ))}

          {/* Add placeholder columns to reach a total of 5 */}
          {framework.fields.length < 5 &&
            Array.from({ length: 5 - framework.fields.length }).map((_, i) => (
              <Column key={i} header={<AddColumnHeader onClick={addField} />} />
            ))}
        </div>
      </div>
    </div>
  );
}
